import { type Nullable, pastaContainer, timeout, useConfig } from "@pasta/box-v2";
import { Deferred } from "@pasta/deferred";
import { useAuthStore } from "@pasta/front-auth-v2";
import io, { Socket } from "socket.io-client";

const pastaWebsocketSymbol = Symbol("pasta-websocket");

export interface PastaSocketEvent {
  type: string;
  data: any;
}

export interface PastaSocketEventMap {
  event: (event: PastaSocketEvent) => void;
}

export interface AppNotificationConfig {
  title: string;
  apiBaseUrl: string;
  socketio: {
    transport: string[];
  };
}

export class PastaWebsocket {
  private socket: Nullable<Socket<PastaSocketEventMap>> = null;
  private deferred: Deferred | null = null;
  private listeners = new Map<string, { name: string; off: Function; cb: Function }[]>();

  private async init(retry = 0) {
    if (this.socket) return;
    if (this.deferred) {
      await this.deferred;
      return;
    }
    if (retry >= 5) {
      throw new Error(`Could not init websocket`);
    }
    this.deferred = new Deferred();
    // eslint-disable-next-line no-async-promise-executor
    this.socket = await new Promise(async (resolve, reject) => {
      // we have evaluated the rule and decided that in this specific case, it's ok
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        let attemps = 0;
        while (!authStore.isAuthenticated && attemps <= 5) {
          await timeout(100);
          attemps += 1;
        }
        reject(new Error("ws - Not authenticated"));
      }

      const config = useConfig<AppNotificationConfig>();
      const ws = io(config.apiBaseUrl, {
        auth: { token: authStore.accessToken },
        transports: config.socketio.transport,
      });
      ws.on("authenticated", () => {
        resolve(ws);
        this.deferred?.resolve();
      });
      ws.once("error", e => {
        reject(e);
        this.deferred?.reject(e);
      });
      ws.once("disconnect", async () => {
        await authStore.fetchPersonalInfos(); // TODO [develop] real refresh token call
        await this.init(retry + 1);
        reject("ws - disconnect");
        this.deferred?.reject("ws - disconnected");
      });
    });
  }

  public async emit(event: string, data: any) {
    await this.init();
    this.socket!.emit("event", { type: event, data });
  }

  /**
   *
   * @param eventType the event type to react to
   * @param uniqName  a uniq name to identify the listener
   * @param callback  the method to invoke
   * @returns
   */
  public async on(eventType: string, uniqName: string, callback: (data: any) => void) {
    await this.init();

    const alreadyAdded = this.listeners.get(eventType)?.some(val => val.name === uniqName);
    if (!alreadyAdded) {
      const cb = (event: PastaSocketEvent) => {
        if (event.type === eventType) {
          callback(event.data);
        }
      };
      const off = () => {
        this.socket!.off("event", cb);
        this.listeners.set(eventType, [
          ...(this.listeners.get(eventType) || []).filter(listener => listener.name !== uniqName),
        ]);
      };

      this.socket!.on("event", cb);
      this.listeners.set(eventType, [...(this.listeners.get(eventType) || []), { name: uniqName, off, cb: callback }]);

      return off;
    }
    return this.listeners.get(eventType)!.find(listener => listener.name === uniqName)!.off;
  }
}

export function usePastaWebsocket(): PastaWebsocket {
  const websocket = pastaContainer.get(pastaWebsocketSymbol);
  if (!websocket) {
    pastaContainer.set(pastaWebsocketSymbol, new PastaWebsocket());
  }
  return pastaContainer.get(pastaWebsocketSymbol);
}
